import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { NumericInput } from "@/components/ui/numeric-input";

export function SplitMaterialDialog({
  open,
  onOpenChange,
  materialPlan,
  orderId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  materialPlan: any;
  orderId: string;
}) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [splitQuantity, setSplitQuantity] = useState<number>(
    Math.floor(materialPlan?.plannedQuantity / 2) || 0,
  );
  const [splitReason, setSplitReason] = useState("");

  const handleSubmit = async () => {
    if (
      !splitQuantity ||
      splitQuantity <= 0 ||
      splitQuantity >= materialPlan.plannedQuantity
    ) {
      toast.error("Số lượng tách không hợp lệ");
      return;
    }

    try {
      setIsSubmitting(true);
      await trpcClient.materialPlanning.splitMaterial.mutate({
        id: materialPlan.id,
        qtyToSplit: splitQuantity,
        splitReason,
      });

      toast.success("Tách vật tư thành công");
      onOpenChange(false);
      queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi tách vật tư");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!materialPlan) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Tách Vật Tư: {materialPlan.plannedMaterial?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-slate-50 p-3 rounded-md border border-slate-100 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Mã vật tư:</span>
              <span className="font-medium">
                {materialPlan.plannedMaterial?.code}
              </span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-slate-500">SL Ban đầu:</span>
              <span className="font-medium font-mono text-indigo-700">
                {materialPlan.plannedQuantity} {materialPlan.unit}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">SL Tách sang dòng mới</label>
            <NumericInput
              value={splitQuantity}
              onChange={(e) => setSplitQuantity(Number(e.target.value))}
              className="text-right font-mono"
            />
            <p className="text-xs text-slate-500 text-right">
              SL còn lại ở dòng cũ:{" "}
              {materialPlan.plannedQuantity - splitQuantity} {materialPlan.unit}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Lý do / Ghi chú tách</label>
            <Input
              value={splitReason}
              onChange={(e) => setSplitReason(e.target.value)}
              placeholder="Vd: Chạy trên 2 khổ giấy khác nhau..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Hủy
          </Button>
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-white"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Đang xử lý..." : "Thực hiện Tách"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
